import React, { useEffect, useState, useMemo } from 'react'
import ApiClient from "../ApiClient";
import moment from 'moment';
import CentralizedTable from '../components/CentralizedTable';
import { selectUser } from "../stores/userSlice";
import { selectIsApiLoading } from "../stores/loadingSlice";
import { useSelector } from "react-redux";
import { CalendarDays, Flag, Calendar as CalendarIcon, ChevronLeft, ChevronRight, Globe } from 'lucide-react';
import { StatCardSkeleton, CalendarSkeleton } from '../components/PageSkeletonLoaders';

const HOLIDAY_COLORS = [
    { bg: 'bg-red-50', text: 'text-red-700', border: 'border-red-200', month: 'text-red-600' },
    { bg: 'bg-blue-50', text: 'text-blue-700', border: 'border-blue-200', month: 'text-blue-600' },
    { bg: 'bg-green-50', text: 'text-green-700', border: 'border-green-200', month: 'text-green-600' },
    { bg: 'bg-purple-50', text: 'text-purple-700', border: 'border-purple-200', month: 'text-purple-600' },
    { bg: 'bg-orange-50', text: 'text-orange-700', border: 'border-orange-200', month: 'text-orange-600' },
    { bg: 'bg-pink-50', text: 'text-pink-700', border: 'border-pink-200', month: 'text-pink-600' },
    { bg: 'bg-teal-50', text: 'text-teal-700', border: 'border-teal-200', month: 'text-teal-600' },
    { bg: 'bg-indigo-50', text: 'text-indigo-700', border: 'border-indigo-200', month: 'text-indigo-600' },
    { bg: 'bg-rose-50', text: 'text-rose-700', border: 'border-rose-200', month: 'text-rose-600' },
    { bg: 'bg-amber-50', text: 'text-amber-700', border: 'border-amber-200', month: 'text-amber-600' },
    { bg: 'bg-cyan-50', text: 'text-cyan-700', border: 'border-cyan-200', month: 'text-cyan-600' },
    { bg: 'bg-emerald-50', text: 'text-emerald-700', border: 'border-emerald-200', month: 'text-emerald-600' },
    { bg: 'bg-fuchsia-50', text: 'text-fuchsia-700', border: 'border-fuchsia-200', month: 'text-fuchsia-600' },
];

let colorIndex = 0;
const occasionColorMap = {};

const getHolidayColor = (occasion) => {
    if (!occasion) return { bg: 'bg-slate-50', text: 'text-slate-700', border: 'border-slate-200', month: 'text-slate-500' };
    
    if (!occasionColorMap[occasion]) {
        occasionColorMap[occasion] = HOLIDAY_COLORS[colorIndex % HOLIDAY_COLORS.length];
        colorIndex++;
    }
    
    return occasionColorMap[occasion];
};

function OfficeHoliday() {
    const user = useSelector(selectUser);
    const isApiLoading = useSelector(selectIsApiLoading);
    const initialTab = user?.country && user.country !== "India" ? '2' : '1';
    const [currentActiveTab, setCurrentActiveTab] = useState(initialTab);
    const [selectedDate, setSelectedDate] = useState(moment());
    
    const [indiaHolidays, setIndiaHolidays] = useState([])
    const [usHolidays, setUsHolidays] = useState([])
    const [allHolidays, setAllHolidays] = useState([])
    const columns = useMemo(() => [{
        headerName: "Day", field: "DATE", sortable: true, flex: 1, 
        renderCell: (params) => {
            const holColor = getHolidayColor(params.row?.OCCASION);
            
            return (
            <div className="flex items-center gap-4">
                <div className={`flex flex-col items-center justify-center w-8 h-8 rounded-xl border text-center leading-none shrink-0 ${holColor.bg} ${holColor.border}`}>
                    <span className={`text-[12px] font-bold mb-[1px] ${holColor.text}`}>{moment(params.row?.DATE).format("DD")}</span>
                    <span className={`text-[8px] font-bold uppercase ${holColor.month}`}>{moment(params.row?.DATE).format("MMM")}</span>
                </div>
                <span className="text-[13px] font-semibold text-slate-700">{moment(params.row?.DATE).format("dddd")}</span>
            </div>
            );
        }
    },{
        headerName: "Occasion", field: "OCCASION", sortable: true, flex: 1,
        renderCell: (params) => (
            <span className="text-[13px] font-medium text-slate-700 ms-0">{params.row?.OCCASION}</span>
        )
    }], []);
    const currentYear = moment().format("YYYY");

    // Calendar state
    const [calendarDate, setCalendarDate] = useState(new Date());

    const getOfficeHolidays = async () => {
        let indiaData = [];
        let usData = [];
        try{
            const {data} = await ApiClient.post(`${process.env.REACT_APP_BASE_URL}/leave/officeholiday`,{
                year: currentYear
            })
            let sortedData = data.sort((a, b) => new Date(a["DATE"]) - new Date(b["DATE"]));

            sortedData?.forEach((el)=>{
                if(el.LOCATION === "India") indiaData.push(el)
                else usData.push(el)
            })
            setIndiaHolidays(indiaData)
            setUsHolidays(usData)
            setAllHolidays(sortedData)
        } catch(error){
            console.log(error)
        }        
    }

    useEffect(() => {
        getOfficeHolidays()
    }, [])

    // Summary calculations
    const upcoming30Days = useMemo(() => {
        const now = moment();
        const thirtyDaysFromNow = moment().add(30, 'days');
        const activeList = currentActiveTab === '1' ? indiaHolidays : usHolidays;
        return activeList.filter(h => {
            const hDate = moment(h.DATE);
            return hDate.isBetween(now, thirtyDaysFromNow, 'day', '[]');
        }).length;
    }, [indiaHolidays, usHolidays, currentActiveTab]);

    const nextHoliday = useMemo(() => {
        const now = moment();
        const activeList = currentActiveTab === '1' ? indiaHolidays : usHolidays;
        const upcoming = activeList.filter(h => moment(h.DATE).isSameOrAfter(now, 'day'));
        return upcoming.length > 0 ? upcoming[0] : null;
    }, [indiaHolidays, usHolidays, currentActiveTab]);

    const totalHolidaysThisYear = useMemo(() => {
        return currentActiveTab === '1' ? indiaHolidays.length : usHolidays.length;
    }, [indiaHolidays, usHolidays, currentActiveTab]);

    // Calendar logic
    const nextMonth = () => setCalendarDate(moment(calendarDate).add(1, 'month').toDate());
    const prevMonth = () => setCalendarDate(moment(calendarDate).subtract(1, 'month').toDate());
    
    const generateCalendarDays = () => {
        const startDay = moment(calendarDate).startOf('month').startOf('week');
        const endDay = moment(calendarDate).endOf('month').endOf('week');
        const day = startDay.clone().subtract(1, 'day');
        const calendar = [];
        
        while (day.isBefore(endDay, 'day')) {
            calendar.push(
                Array(7).fill(0).map(() => day.add(1, 'day').clone())
            );
        }
        return calendar;
    };

    const handleDateClick = (day) => {
        setSelectedDate(day);
    };

    const getHoliday = (date, location) => {
        const list = location === 'India' ? indiaHolidays : usHolidays;
        return list.find(h => moment(h.DATE).isSame(date, 'day'));
    };

    return (
        <div className="flex flex-col gap-3">
            {/* Top Summary Cards */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
                {isApiLoading ? (
                    <>
                        <StatCardSkeleton />
                        <StatCardSkeleton />
                        <StatCardSkeleton />
                        <StatCardSkeleton />
                    </>
                ) : (
                    <>
                        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-4 flex items-center gap-3">
                            <div className="w-10 h-10 rounded-2xl bg-blue-50 text-blue-500 flex items-center justify-center shrink-0">
                                <CalendarDays className="w-5 h-5" />
                            </div>
                            <div>
                                <h4 className="text-[11px] font-bold text-slate-500 uppercase tracking-wider mb-0.5">Upcoming Holidays</h4>
                                <div className="flex items-end gap-1.5">
                                    <span className="text-xl font-bold text-slate-800 leading-none">{upcoming30Days}</span>
                                    <span className="text-[11px] font-medium text-slate-500 mb-0.5">Days</span>
                                </div>
                                <p className="text-[10px] font-medium text-slate-400 mt-1">In next 30 days</p>
                            </div>
                        </div>

                        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-4 flex items-center gap-3">
                            <div className="w-10 h-10 rounded-2xl bg-emerald-50 text-emerald-500 flex items-center justify-center shrink-0">
                                <Flag className="w-5 h-5" />
                            </div>
                            <div>
                                <h4 className="text-[11px] font-bold text-slate-500 uppercase tracking-wider mb-0.5">Next Holiday</h4>
                                <div className="text-lg font-bold text-slate-800 leading-tight">
                                    {nextHoliday ? moment(nextHoliday.DATE).format("DD MMM YYYY") : "None"}
                                </div>
                                <p className="text-[11px] font-medium text-slate-500 mt-1 truncate max-w-[200px]">
                                    {nextHoliday ? nextHoliday.OCCASION : "No upcoming holidays"}
                                </p>
                            </div>
                        </div>

                        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-4 flex items-center gap-3">
                            <div className="w-10 h-10 rounded-2xl bg-orange-50 text-orange-500 flex items-center justify-center shrink-0">
                                <CalendarIcon className="w-5 h-5" />
                            </div>
                            <div>
                                <h4 className="text-[11px] font-bold text-slate-500 uppercase tracking-wider mb-0.5">This Year</h4>
                                <div className="text-xl font-bold text-slate-800 leading-none">
                                    {totalHolidaysThisYear}
                                </div>
                                <p className="text-[10px] font-medium text-slate-400 mt-1">Total Holidays</p>
                            </div>
                        </div>
                        
                        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-4 flex items-center gap-3">
                            <div className="w-10 h-10 rounded-2xl bg-purple-50 text-purple-600 flex items-center justify-center shrink-0">
                                <Globe className="w-5 h-5" />
                            </div>
                            <div>
                                <h4 className="text-[11px] font-bold text-slate-500 uppercase tracking-wider mb-0.5">Holiday Calendar </h4>
                                <div className="text-lg font-bold text-slate-800 leading-tight">
                                    {currentActiveTab === '1' ? 'India' : 'US'}
                                </div>
                                <p className="text-[11px] font-medium text-slate-500 mt-1">Region</p>
                            </div>
                        </div>
                    </>
                )}
            </div>

            {/* Bottom Section */}
            <div className="flex flex-col xl:flex-row gap-3">
                {/* List Card */}
                <div className="flex-[2] bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden flex flex-col">


                    <div className="flex-1">
                        <CentralizedTable
                             getRowId={(row,index) => {
                                return row?.HOLIDAY_ID;
                            }}
                            rows={currentActiveTab === '1' ? indiaHolidays : usHolidays}
                            columns={columns}
                            isLoading={isApiLoading}
                            autoHeight
                            muiTableBodyRowProps={({ row }) => {
                                if(!row.original.DATE) return;
                                const isSelected = selectedDate.isSame(moment(row.original.DATE), 'day');
                                return {
                                    onClick: () => {
                                        const day = moment(row.original.DATE);
                                        setSelectedDate(day);
                                        setCalendarDate(day.toDate());
                                    },
                                    sx: {
                                        cursor: 'pointer',
                                        backgroundColor: isSelected ? '#eff6ff' : undefined,
                                        '&:hover': {
                                            backgroundColor: isSelected ? '#eff6ff' : '#f8fafc',
                                        }
                                    }
                                };
                            }}
                            initialState={{
                                pagination: { paginationModel: { pageSize: 10, page: 0 } },
                            }}
                            renderTopToolbarCustomActions={() => (
                                   <div className="flex items-center h-12">
                                    <button
                                        type="button"
                                        onClick={() => setCurrentActiveTab('1')}
                                        className={`relative h-full px-5 text-sm font-medium transition-colors duration-200 ${
                                           currentActiveTab === '1'
                                            ? 'text-brand-600'
                                            : 'text-slate-500 hover:text-slate-700'
                                        }`}
                                        >
                                                India Holidays
                                                 {currentActiveTab === '1' && (
                                            <span className="absolute bottom-[-4px] left-0 h-[2px] w-full bg-brand-600" style={{ marginBottom: '-1px' }}></span>
                                        )}
                                    </button>

                                    <button
                                        type="button"
                                        onClick={() => setCurrentActiveTab('2')}
                                        className={`relative h-full px-5 text-sm font-medium transition-colors duration-200 ${
                                           currentActiveTab === '2'
                                                ? 'text-brand-600'
                                                : 'text-slate-500 hover:text-slate-700'
                                        }`}
                                    >
                                       US Holidays
                                         {currentActiveTab === '2' && (
                                            <span className="absolute bottom-[-4px] left-0 h-[2px] w-full bg-brand-600" style={{ marginBottom: '-1px' }}></span>
                                        )}
                                    </button>
                                </div>
                            )}
                             muiTableBodyCellProps={({ cell }) => ({
                                sx: {
                                    py: "7px",
                                    px: cell.column.getIndex() === 0 ? "16px" : "3px",
                                    borderBottom: "1px solid #e2e8f0",
                                },
                                })}
                                 muiTableContainerProps={{
                                        sx: {
                                            minHeight: (currentActiveTab === '1' ? indiaHolidays : usHolidays).length > 0 ? '420px' : "auto",
                                            height: 'auto',
                                            flex: 1,
                                            overflow: 'auto'
                                        },
                                    }}
                            pageSizeOptions={[10, 20]}
                            hideFooterSelectedRowCount 
                        />
                    </div>
                </div>

                {/* Calendar Card */}
                {isApiLoading ? (
                    <div className="flex-1 bg-white rounded-2xl shadow-sm border border-slate-200 p-6 flex flex-col min-w-[320px]">
                    <CalendarSkeleton />
                    </div>
                ) : (
                    <div className="flex-1 bg-white rounded-2xl shadow-sm border border-slate-200 p-6 flex flex-col min-w-[320px]">
                        <div className="flex items-center justify-between mb-8">
                            <h3 className="text-[15px] font-bold text-slate-800 m-0 flex items-center gap-3">
                                <div className="p-1.5 bg-purple-100 text-purple-600 rounded-lg">
                                    <CalendarIcon size={18} />
                                </div>
                                Office Holiday Calendar 
                            </h3>
                        </div>

                        <div className="flex-1">
                            <div className="flex items-center justify-between mb-6">
                                <button onClick={prevMonth} className="p-1.5 rounded-lg hover:bg-slate-100 text-slate-600 transition-colors">
                                    <ChevronLeft className="w-5 h-5" />
                                </button>
                                <h4 className="text-[17px] font-bold text-slate-800 m-0">
                                    {moment(calendarDate).format("MMMM YYYY")}
                                </h4>
                                <button onClick={nextMonth} className="p-1.5 rounded-lg hover:bg-slate-100 text-slate-600 transition-colors">
                                    <ChevronRight className="w-5 h-5" />
                                </button>
                            </div>

                            <div className="grid grid-cols-7 gap-y-4 mb-4">
                                {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(d => (
                                    <div key={d} className="text-center text-xs font-bold text-slate-400 uppercase tracking-wider">
                                        {d}
                                    </div>
                                ))}
                            </div>

                            <div className="grid grid-cols-7 gap-y-3 text-center">
                                {generateCalendarDays().map((week, i) => (
                                    <React.Fragment key={i}>
                                        {week.map((day, j) => {
                                            const isCurrentMonth = day.isSame(calendarDate, 'month');
                                            const isIndiaHol = getHoliday(day, 'India');
                                            const isUsHol = getHoliday(day, 'US');
                                            const activeHol = currentActiveTab === '1' ? isIndiaHol : isUsHol;
                                            const holColor = activeHol ? getHolidayColor(activeHol.OCCASION) : null;
                                            const isToday = day.isSame(moment(), 'day');
                                            const isSelected = day.isSame(selectedDate, 'day');

                                            return (
                                                <div key={j} className="flex flex-col items-center justify-start h-12">
                                                    <div 
                                                        onClick={() => handleDateClick(day)}
                                                        className={`w-8 h-8 flex items-center justify-center rounded-full text-sm font-semibold transition-all cursor-pointer ${
                                                        isSelected ? 'ring-2 ring-offset-1 ring-blue-600 ' : ''
                                                    } ${
                                                        isToday ? 'bg-brand-50 text-brand-700 border border-brand-200' : 
                                                        activeHol ? `${holColor.bg} ${holColor.text} border ${holColor.border}` :
                                                        !isCurrentMonth ? 'text-slate-300' : 'text-slate-700 hover:bg-slate-50'
                                                    }`} title={activeHol ? activeHol.OCCASION : undefined}>
                                                        {day.format('D')}
                                                    </div>
                                                    <div className="flex gap-1 mt-1">
                                                        {isIndiaHol && <span className="w-1.5 h-1.5 rounded-full bg-blue-500" title="India Holiday"></span>}
                                                        {isUsHol && <span className="w-1.5 h-1.5 rounded-full bg-orange-500" title="US Holiday"></span>}
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </React.Fragment>
                                ))}
                            </div>
                        </div>

                        <div className="mt-4 flex flex-col items-center justify-center gap-2 pb-2">
                            <div className="text-[11px] font-medium text-slate-500 text-center mb-1">
                                Match calendar colors with the table to easily identify holidays
                            </div>
                            <div className="flex items-center gap-6">
                                <div className="flex items-center gap-2">
                                    <span className="w-2 h-2 rounded-full bg-blue-500"></span>
                                    <span className="text-[11px] font-bold text-slate-700">India Holiday</span>
                                </div>
                                <div className="flex items-center gap-2">
                                    <span className="w-2 h-2 rounded-full bg-orange-500"></span>
                                    <span className="text-[11px] font-bold text-slate-700">US Holiday</span>
                                </div>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    )
}

export default OfficeHoliday
